import { useNavigate } from "react-router-dom";
import { Card } from "@mantine/core";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { EmptyState, ErrorState, NotProjectedState } from "@/components/states";
import { isPermissionError } from "@/lib/errors";
import { usePaginatedQuery } from "@/firestore/hooks";
import { isNotYetProjected } from "@/firestore/collections";
import type { WithDocId } from "@/firestore/types";
import { entityHasDetail, entityPath } from "@/routes/paths";
import type { AnyEntityConfig } from "@/entities/types";

/** Generic list view driven by an {@link EntityConfig}. Live, sortable, filterable. */
export function EntityListPage({ config }: { config: AnyEntityConfig }) {
  const navigate = useNavigate();
  const notProjected = isNotYetProjected(config.collection);

  const { data, loading, error, hasMore, loadMore } = usePaginatedQuery<Record<string, unknown>>(
    config.collection,
    { orderBy: config.defaultOrder },
    50,
  );

  const clickable = entityHasDetail(config.key);

  return (
    <>
      <PageHeader title={config.pluralTitle} />
      <Card withBorder radius="md" padding="md">
        {error ? (
          // A not-yet-projected collection has no security rules deployed either,
          // so its live query is denied — render the explanatory empty state, not an error.
          notProjected && isPermissionError(error) ? (
            <NotProjectedState />
          ) : (
            <ErrorState error={error} />
          )
        ) : (
          <DataTable
            data={data}
            columns={config.columns}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={loadMore}
            searchPlaceholder={`Filter ${config.pluralTitle.toLowerCase()}…`}
            onRowClick={
              clickable
                ? (row) => navigate(entityPath(config.key, (row as WithDocId<unknown>)._docId))
                : undefined
            }
            emptyState={
              notProjected ? (
                <NotProjectedState />
              ) : (
                <EmptyState
                  title={`No ${config.pluralTitle.toLowerCase()}`}
                  description="Nothing matches yet. New rows appear here live."
                />
              )
            }
          />
        )}
      </Card>
    </>
  );
}
